import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig"; // Update path as needed
import { devLog } from "./logging";
import { toast } from "react-toastify";

const API_SCOPE = process.env.REACT_APP_FUNCTION_APP_CLIENT_ID
  ? `api://${process.env.REACT_APP_FUNCTION_APP_CLIENT_ID}/access`
  : "User.Read";

const rawUrl = process.env.REACT_APP_FUNCTION_APP_URL || "https://localhost:7093/";
const API_BASE_URL = rawUrl.endsWith("/") ? rawUrl : rawUrl + "/";

const msalInstance = new PublicClientApplication(msalConfig);

let msalReady = false;
let currentAccount = null;
let hasShownApiDownToast = false;

export const ensureMsalReady = async () => {
  if (!msalReady) {
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) throw new Error("No MSAL account found.");
    currentAccount = accounts[0];
    msalReady = true;
  }
};

export const getCurrentUser = async () => {
  await ensureMsalReady();
  if (!currentAccount) throw new Error("No MSAL account found.");

  return {
    // Backend uses User.Identity.Name which is typically the email/username
    userId: currentAccount.username, // This should match User.Identity.Name in .NET
    username: currentAccount.username,
    name: currentAccount.name,
    email: currentAccount.username, // Usually the email
    // Additional user ID formats that backend might expect
    allUserIds: {
      localAccountId: currentAccount.localAccountId,
      homeAccountId: currentAccount.homeAccountId,
      username: currentAccount.username,
      oid: currentAccount.idTokenClaims?.oid, // Object ID from Azure AD
      sub: currentAccount.idTokenClaims?.sub, // Subject claim
      upn: currentAccount.idTokenClaims?.upn, // User Principal Name
      preferred_username: currentAccount.idTokenClaims?.preferred_username,
    },
  };
};

export const getAccessToken = async () => {
  await ensureMsalReady();
  if (!currentAccount) throw new Error("No MSAL account found.");

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: currentAccount,
    });
    return tokenResponse.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const tokenResponse = await msalInstance.acquireTokenPopup({
        scopes: [API_SCOPE],
      });
      return tokenResponse.accessToken;
    } else {
      throw error;
    }
  }
};

export const callAPI = async (
  apiEndpoint,
  method,
  body,
  json = true,
  downloadFile = false,
  filename = "download.csv"
) => {
  await ensureMsalReady();
  if (!currentAccount) throw new Error("No MSAL account found.");

  let tokenResponse;
  try {
    tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: currentAccount,
    });
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      tokenResponse = await msalInstance.acquireTokenPopup({
        scopes: [API_SCOPE],
      });
    } else {
      toast.error("❌ Authentication failed.");
      return []; // return empty data on auth error
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}api/${apiEndpoint}`, {
      method: method || "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenResponse.accessToken}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (downloadFile) {
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`✅ File (${filename}) downloaded.`);
      return; // nothing to return
    }

    if (method === "POST") return response;

    if (json) {
      const data = await response.json();
      devLog(apiEndpoint, data);

      if (data?.value) {
        if (data.value.syncing) {
          toast.info(data.value.message || "Data is syncing in the background...");
        }
        if (data.value.isError) {
          toast.error(data.value.message || "An error occurred while processing your request.");
        }

        if (data.value.data !== undefined && data.value.data !== null) {
          return data.value.data;
        } else if (data.value) {
          return data.value;
        } else {
          return [];
        }
      } else if (data) {
        return data;
      }

      return [];
    }

    return response;
  } catch (error) {
    if (!hasShownApiDownToast) {
      toast.error("API call failed, API is not available.");
      hasShownApiDownToast = true;
      setTimeout(() => {
        hasShownApiDownToast = false;
      }, 90000);
    }
    devLog("API Error", error);
    return [];
  }
};
